import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';
import _ from 'lodash';

import {
  GiftOrder,
  GiftOrderBillingCard,
  GiftOrderCancel,
  GiftOrderCourtesyUpdate,
  GiftOrderCreateDto,
  GiftOrderPartialCancel,
  GiftOrderPartialCancelResponse,
  GiftOrderUpdate,
  GiftStoreRole,
  SetBillingCardDto,
} from './giftOrder.interface';
import {
  CoffinOutDailyResponse,
  CoffinOutFeventStatsResponse,
  CoffinOutPacingResponse,
  GiftConversionInterval,
  GiftConversionResponse,
  GiftPacingResponse,
  CustomerAnalyticsItemType,
  CustomerAnalyticsResponse,
  DashboardSalesDetailResponse,
  DashboardSummaryDetailResponse,
  DashboardSummaryResponse,
  ProfitAnalysisDetailResponse,
  ProfitAnalysisResponse,
  WindfallCohortResponse,
  WindfallCouponResponse,
  WindfallDeliveryResponse,
} from './giftOrderDashboard.interface';

const validRolesCreateGiftOrder = [LowerRole.StoreCustomer, LowerRole.StoreGuest];

/**
 * GiftOrder 생성
 */
export const createGiftOrder = async (
  giftOrderDto: GiftOrderCreateDto,
  storeRole: GiftStoreRole,
) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const postGiftOrderResponse = await api_.post<GiftOrder>(
      `/${storeRole}/gift-order/create`,
      giftOrderDto,
    );
    return postGiftOrderResponse;
  }
};

/**
 * GiftOrder 결제 검증
 */
export const verifyGiftOrderById = async (
  id: string,
  storeRole: GiftStoreRole,
  receiptId: string,
) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const { data: isVerify } = await api_.get<boolean>(
      `${storeRole}/gift-order/success/${id}?${queryString({ receiptId })}`,
    );
    return isVerify;
  }
};

/**
 * GiftOrder 삭제 (결제 취소/실패 시)
 */
export const deleteGiftOrderById = async (id: string, storeRole: GiftStoreRole) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    return await api_.delete<GiftOrder>(`${storeRole}/gift-order/delete/${id}`);
  }
};

/**
 * GiftOrder 수정
 */
export const updateGiftOrder = async (
  giftOrderUpdate: GiftOrderUpdate,
  role: LowerRole,
) => {
  if (role === LowerRole.Admin) {
    const { data } = await api_.patch(
      `/${role}/gift-order/${giftOrderUpdate._id}`,
      giftOrderUpdate,
    );
    return { data };
  } else {
    const { data } = await api_.patch(
      `/${role}/gift-order/patch-detail/${giftOrderUpdate._id}`,
      giftOrderUpdate,
    );
    return { data };
  }
};

/**
 * GiftOrder 취소
 */
export const cancelGiftOrder = async (giftOrder: GiftOrderCancel, role: LowerRole) => {
  const { data } = await api_.patch(
    `${role}/gift-order/cancel/${giftOrder._id}`,
    giftOrder,
  );
  return { data };
};

/**
 * GiftOrder 부분취소 (Admin 전용)
 */
export const partialCancelGiftOrder = async (dto: GiftOrderPartialCancel) => {
  const { data } = await api_.patch<GiftOrderPartialCancelResponse>(
    `${LowerRole.Admin}/gift-order/partial-cancel/${dto._id}`,
    {
      cancelReason: dto.cancelReason,
      couponIds: dto.couponIds,
      deliveryIds: dto.deliveryIds,
      // 가상계좌/입금 수기환불 취소 시에만 존재 (카드는 undefined → 미포함)
      manualRefund: dto.manualRefund,
    },
  );
  return { data };
};

/**
 * 가상계좌 알림 발송
 */
export const sendGiftVBankAlimUser = async (id: string, vBank: string) => {
  const result = await api_.post(`public/gift-order/send-v-bank-message/${id}`, {
    vBank,
  });
  return result;
};

/**
 * GiftOrder 목록 조회 (by bugoId)
 */
export const getGiftOrderListByBugo = async (bugoId: string) => {
  const { data } = await api_.get<GiftOrder[]>(`public/gift-order/by-bugo/${bugoId}`);
  return data;
};

/**
 * GiftOrder 답례글 업데이트
 */
export const updateGiftOrderCourtesy = async (
  orderId: string,
  dto: GiftOrderCourtesyUpdate,
  role: GiftStoreRole,
) => {
  const { data } = await api_.patch<GiftOrder>(`/${role}/gift-order/${orderId}`, dto);
  return data;
};

// ─── 추가교환 충당용 빌링카드 (Admin 전용) ───

/** 빌링카드 조회 — 없으면 null */
export const getBillingCard = async (
  orderId: string,
): Promise<GiftOrderBillingCard | null> => {
  const { data } = await api_.get<GiftOrderBillingCard | null>(
    `/${LowerRole.Admin}/gift-order/${orderId}/billing-card`,
  );
  return data;
};

/** 빌링카드 등록/수정 */
export const setBillingCard = async (
  orderId: string,
  dto: SetBillingCardDto,
): Promise<{ success: boolean }> => {
  const { data } = await api_.patch<{ success: boolean }>(
    `/${LowerRole.Admin}/gift-order/${orderId}/billing-card`,
    dto,
  );
  return data;
};

/** 빌링카드 삭제 */
export const deleteBillingCard = async (
  orderId: string,
): Promise<{ success: boolean }> => {
  const { data } = await api_.delete<{ success: boolean }>(
    `/${LowerRole.Admin}/gift-order/${orderId}/billing-card`,
  );
  return data;
};

export type DashboardRole = LowerRole.Admin | LowerRole.BugoAgencyAdmin;

const dashboardUrl = (
  path: string,
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => `${role}/gift-order/dashboard/${path}?${queryString({ from, to })}`;

export const getDashboardSummary = async (
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<DashboardSummaryResponse>(
    dashboardUrl('summary', from, to, role),
  );
  return data;
};

/**
 * 매출 구성 요약을 주문별로 펼친 것. 요약탭 엑셀 저장 전용.
 *
 * ⚠️ 백엔드 미배포 — 없으면 404. 호출부가 404를 안내로 바꿔 처리한다.
 * `sales-detail`과 헷갈리지 말 것: 그쪽은 전체취소 주문이 빠져 있어 요약탭과 대사가 안 된다.
 */
export const getDashboardSummaryDetail = async (
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<DashboardSummaryDetailResponse>(
    dashboardUrl('summary-detail', from, to, role),
  );
  return data;
};

export const getDashboardSalesDetail = async (
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<DashboardSalesDetailResponse>(
    dashboardUrl('sales-detail', from, to, role),
  );
  return data;
};

/**
 * GiftOrder 강제 삭제 (테스트용 Admin 전용)
 * 연결된 GiftCoupon, GiftDelivery 모두 삭제 후 GiftOrder 삭제
 */
export const forceDeleteGiftOrder = async (id: string) => {
  const { data } = await api_.delete<{ message: string }>(
    `${LowerRole.Admin}/gift-order/force-delete/${id}`,
  );
  return data;
};

export const getDashboardProfitAnalysis = async (
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<ProfitAnalysisResponse>(
    dashboardUrl('profit-analysis', from, to, role),
  );
  return data;
};

export const getDashboardProfitAnalysisDetail = async (
  from: string,
  to: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<ProfitAnalysisDetailResponse>(
    dashboardUrl('profit-analysis-detail', from, to, role),
  );
  return data;
};

/**
 * 구매자(user) 단위 구매행태 분석. 스펙: docs/gift-order-customer-analytics-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 * PII(topBuyers.name/phoneNumber)는 Admin 응답에만 실린다.
 *
 * itemType 필터(v2) 사용 시 구매액 축이 결제축 → 해당 유형 라인축 상품금액으로 바뀐다.
 * 무필터 응답의 결제축 숫자와 단순 합산 비교하지 말 것.
 */
export const getDashboardCustomerAnalytics = async (
  from: string,
  to: string,
  top?: number,
  itemType?: CustomerAnalyticsItemType,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<CustomerAnalyticsResponse>(
    `${role}/gift-order/dashboard/customer-analytics?${queryString({
      from,
      to,
      top,
      itemType,
    })}`,
  );
  return data;
};

/**
 * 발인일(KST) 기준 일별 상가·화환·답례품 통계 — 달력/추이 차트용.
 * 매출은 주문일이 아니라 해당 fevent의 발인일로 귀속된다.
 * 스펙: docs/gift-coffin-out-daily-stats-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 * teamType은 콤마 분리 다중값 가능 (현재 UI는 단일 선택만 사용).
 */
export const getDashboardCoffinOutDaily = async (
  from: string,
  to: string,
  bugoBrandId?: string,
  teamType?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<CoffinOutDailyResponse>(
    `${role}/gift-order/dashboard/coffin-out-daily?${queryString({
      from,
      to,
      bugoBrandId,
      teamType,
    })}`,
  );
  return data;
};

/**
 * 발인일 범위의 상가(fevent) 단위 실적 — "화환 많은 상가가 답례품도 많이 사는가"
 * 상관 분석용 원자료. 상가 1곳 = 행 1개, 식별자·날짜 미포함.
 * 스펙: docs/gift-coffin-out-daily-stats-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 */
export const getDashboardCoffinOutFeventStats = async (
  from: string,
  to: string,
  bugoBrandId?: string,
  teamType?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<CoffinOutFeventStatsResponse>(
    `${role}/gift-order/dashboard/coffin-out-fevent-stats?${queryString({
      from,
      to,
      bugoBrandId,
      teamType,
    })}`,
  );
  return data;
};

/**
 * 진행 중 발인 코호트(D-2~D+3)의 화환 판매 페이스 — 과거 8주 구매 타이밍 곡선을
 * 학습해 "지금쯤 팔렸어야 할 양" 대비 실적을 비교. 기간 파라미터 없음(창은 서버 고정).
 * 스펙: docs/gift-coffin-out-daily-stats-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 */
export const getDashboardCoffinOutPacing = async (
  bugoBrandId?: string,
  teamType?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const qs = queryString({ bugoBrandId, teamType });
  const { data } = await api_.get<CoffinOutPacingResponse>(
    `${role}/gift-order/dashboard/coffin-out-pacing${qs ? `?${qs}` : ''}`,
  );
  return data;
};

/**
 * 상가 단위 답례품 퍼널·전환율·분포·기대치 (발인일 축).
 * "발인 상가 → 부고 공유 → 스토어 조회(이벤트 로그) → 구매" 단계별 전환과,
 * 조회 구간 직전 1년 기준선 대비 달성률을 함께 준다.
 * 스펙: docs/gift-conversion-analysis-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 */
export const getDashboardGiftConversion = async (
  from: string,
  to: string,
  bugoBrandId?: string,
  teamType?: string,
  interval?: GiftConversionInterval,
  role: DashboardRole = LowerRole.Admin,
) => {
  const { data } = await api_.get<GiftConversionResponse>(
    `${role}/gift-order/dashboard/gift-conversion?${queryString({
      from,
      to,
      bugoBrandId,
      teamType,
      interval,
    })}`,
  );
  return data;
};

/**
 * 최근 발인 코호트(45일)의 답례품 판매 페이스 — "지금쯤 몇 건 팔렸어야 하나".
 * 답례품은 발인 후 30일까지 팔려서, 성숙도 보정 없이는 최근 실적이 늘 미달로 보인다.
 * 기간 파라미터 없음(창은 서버 고정).
 * 스펙: docs/gift-conversion-analysis-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 */
export const getDashboardGiftPacing = async (
  bugoBrandId?: string,
  teamType?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const qs = queryString({ bugoBrandId, teamType });
  const { data } = await api_.get<GiftPacingResponse>(
    `${role}/gift-order/dashboard/gift-pacing${qs ? `?${qs}` : ''}`,
  );
  return data;
};

export const getCouponWindfallAnalysis = async (
  from?: string,
  to?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const params = from && to ? `?${queryString({ from, to })}` : '';
  const { data } = await api_.get<WindfallCouponResponse>(
    `${role}/gift-coupon/windfall-analysis${params}`,
  );
  return data;
};

/**
 * 낙전 코호트 — 판매월(purchasedAt, KST)별로 이후 월마다 발송/낙전환불로
 * 몇 건이 소진되고 얼마나 잔존하는지. 쿠폰 전용.
 * 스펙: docs/gift-coupon-windfall-cohort-spec.md
 *
 * ⚠️ 백엔드 배포 확인 전 — 구서버는 404. 호출부가 404를 안내로 바꿔 처리한다.
 * from/to는 판매월(코호트) 범위. 둘 다 없으면 전체 코호트.
 */
export const getCouponWindfallCohort = async (
  from?: string,
  to?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const params = from && to ? `?${queryString({ from, to })}` : '';
  const { data } = await api_.get<WindfallCohortResponse>(
    `${role}/gift-coupon/windfall-cohort${params}`,
  );
  return data;
};

export const getDeliveryWindfallAnalysis = async (
  from?: string,
  to?: string,
  role: DashboardRole = LowerRole.Admin,
) => {
  const params = from && to ? `?${queryString({ from, to })}` : '';
  const { data } = await api_.get<WindfallDeliveryResponse>(
    `${role}/gift-delivery/windfall-analysis${params}`,
  );
  return data;
};
